import { useState, useEffect, useCallback } from 'react';
import {
  Wallet,
  Loader2,
  AlertCircle,
  Plus,
  X,
  CheckCircle2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type BudgetEntry, type BudgetVariance } from '@/types';
import { periodsApi, budgetApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

function formatPct(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

const budgetTabs = [
  { key: 'budget' as const, label: 'Budget' },
  { key: 'forecast' as const, label: 'Forecast' },
  { key: 'what_if' as const, label: 'What-If' },
];

// --- Create Entry Modal ------------------------------------------------------

interface EntryModalProps {
  periodId: string;
  budgetType: BudgetEntry['budget_type'];
  onClose: () => void;
  onSave: (data: Partial<BudgetEntry>) => Promise<void>;
}

function EntryModal({ periodId, budgetType, onClose, onSave }: EntryModalProps) {
  const [entityId, setEntityId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave({
        entity_id: Number(entityId),
        period_id: Number(periodId),
        group_account_id: Number(accountId),
        budget_type: budgetType,
        amount: Number(amount),
        currency,
        notes,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Add {budgetType.charAt(0).toUpperCase() + budgetType.slice(1).replace('_', ' ')} Entry
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Entity ID</label>
              <input
                type="number"
                className="input-field w-full text-sm"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder="Entity ID"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Account ID</label>
              <input
                type="number"
                className="input-field w-full text-sm"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="Group Account ID"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Amount</label>
              <input
                type="number"
                className="input-field w-full text-sm"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Currency</label>
              <select
                className="select-field w-full text-sm"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="SAR">SAR</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Notes</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !entityId || !accountId || !amount}
            className="btn-primary text-sm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add Entry
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function BudgetForecast() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Budget type tab
  const [activeType, setActiveType] = useState<BudgetEntry['budget_type']>('budget');

  // Data
  const [entries, setEntries] = useState<BudgetEntry[]>([]);
  const [variance, setVariance] = useState<BudgetVariance[]>([]);

  // UI
  const [showModal, setShowModal] = useState(false);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [entriesRes, varianceRes] = await Promise.all([
        budgetApi.list(Number(selectedPeriod)).catch(() => []),
        budgetApi.variance(Number(selectedPeriod)).catch(() => []),
      ]);
      setEntries(Array.isArray(entriesRes) ? entriesRes : []);
      setVariance(Array.isArray(varianceRes) ? varianceRes : []);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load budget data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadData();
  }, [loadData, selectedPeriod]);

  // Filtered entries by type
  const filteredEntries = entries.filter((e) => e.budget_type === activeType);

  // Save entry
  const handleSave = async (data: Partial<BudgetEntry>) => {
    try {
      await budgetApi.create(data);
      addNotification({ type: 'success', title: 'Entry Added', message: 'Budget entry has been created.' });
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Save Failed', message: apiErr.detail || 'Failed to create entry.' });
    }
  };

  // Approve entry
  const handleApprove = async (id: number) => {
    try {
      await budgetApi.approve(id);
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, is_approved: true } : e)));
      addNotification({ type: 'success', title: 'Entry Approved', message: 'Budget entry has been approved.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Approval Failed', message: apiErr.detail || 'Failed to approve entry.' });
    }
  };

  // Variance chart data
  const varianceChartData = variance.slice(0, 10).map((v) => ({
    account: v.account_name.length > 18 ? v.account_name.slice(0, 16) + '...' : v.account_name,
    budget: v.budget_amount,
    actual: v.actual_amount,
  }));

  // Skeleton loading
  if (isLoading && entries.length === 0 && variance.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse"><div className="skeleton w-full h-64" /></div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Budget & Forecast</h1>
            <p className="text-sm text-gray-500 mt-0.5">Budget planning, forecasting, and variance analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Budget Type Tabs */}
      <div className="flex items-center gap-2">
        {budgetTabs.map((tab) => {
          const count = entries.filter((e) => e.budget_type === tab.key).length;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveType(tab.key)}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                activeType === tab.key
                  ? 'bg-primary-700 text-white'
                  : 'text-gray-600 hover:bg-gray-200 bg-gray-100',
              )}
            >
              {tab.label}
              <span className={clsx(
                'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                activeType === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 text-gray-600',
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Budget Entries Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            {budgetTabs.find((t) => t.key === activeType)?.label} Entries
          </h2>
          <span className="text-xs text-gray-500">{filteredEntries.length} entries</span>
        </div>
        {filteredEntries.length === 0 ? (
          <div className="py-12 text-center">
            <Wallet className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No {activeType} entries for this period.</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary mt-3 text-sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Entry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="table-header">Entity</th>
                  <th className="table-header">Account</th>
                  <th className="table-header text-right">Amount</th>
                  <th className="table-header">Currency</th>
                  <th className="table-header">Notes</th>
                  <th className="table-header w-24">Status</th>
                  <th className="table-header w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="table-cell text-sm text-gray-800">{entry.entity_name}</td>
                    <td className="table-cell text-sm text-gray-600">{entry.account_name}</td>
                    <td className="table-cell text-right font-mono text-sm text-gray-900">
                      {formatAmount(entry.amount)}
                    </td>
                    <td className="table-cell text-sm text-gray-600">{entry.currency}</td>
                    <td className="table-cell text-sm text-gray-500 max-w-xs truncate">
                      {entry.notes || '--'}
                    </td>
                    <td className="table-cell">
                      {entry.is_approved ? (
                        <span className="badge bg-green-50 text-green-700 gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Approved
                        </span>
                      ) : (
                        <span className="badge bg-amber-50 text-amber-700">Pending</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {!entry.is_approved && (
                        <button
                          onClick={() => handleApprove(entry.id)}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Variance Analysis */}
      {variance.length > 0 && (
        <>
          {/* Variance Chart */}
          <div className="card p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Budget vs Actual Variance</h2>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={varianceChartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="account"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickFormatter={(v: number) => formatAmount(v)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                  formatter={(value: number) => [formatAmount(value)]}
                />
                <Legend />
                <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Variance Table */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900">Variance Details</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Account</th>
                    <th className="table-header text-right">Budget</th>
                    <th className="table-header text-right">Actual</th>
                    <th className="table-header text-right">Variance</th>
                    <th className="table-header text-right">Variance %</th>
                  </tr>
                </thead>
                <tbody>
                  {variance.map((v) => {
                    const favorable = v.variance >= 0;
                    return (
                      <tr
                        key={v.account_id}
                        className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="table-cell text-sm font-medium text-gray-800">{v.account_name}</td>
                        <td className="table-cell text-right font-mono text-sm text-gray-700">
                          {formatAmount(v.budget_amount)}
                        </td>
                        <td className="table-cell text-right font-mono text-sm text-gray-700">
                          {formatAmount(v.actual_amount)}
                        </td>
                        <td className={clsx(
                          'table-cell text-right font-mono text-sm font-semibold',
                          favorable ? 'text-green-700' : 'text-red-700',
                        )}>
                          {formatAmount(v.variance)}
                        </td>
                        <td className={clsx(
                          'table-cell text-right font-mono text-sm font-semibold',
                          favorable ? 'text-green-700' : 'text-red-700',
                        )}>
                          <span className={clsx(
                            'badge text-xs',
                            favorable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
                          )}>
                            {formatPct(v.variance_pct)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create Entry Modal */}
      {showModal && (
        <EntryModal
          periodId={selectedPeriod}
          budgetType={activeType}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
